import { Inject, Injectable } from '@angular/core';
import { PageService } from '../page.service';
import { HttpService } from '../../services/http.service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})

export class CommonService {
  // Page related config
  pageConfig: any = this.pageService.config;
  destroySubject$: Subject<void> = new Subject();
  hpDotComCcLcMappingSub = new BehaviorSubject<any>({});
  contexualSecNavContactHpLink = new BehaviorSubject<any>({});
  typeAheadURL = new BehaviorSubject<any>({ typeAheadURL: null });
  typeAheadURL$ = this.typeAheadURL.asObservable();
  productTypeAheadURL = new BehaviorSubject<any>({ typeAheadURL: null });
  productTypeAheadURL$ = this.productTypeAheadURL.asObservable();
  paginationKeys = new BehaviorSubject<any>({ paginationKeys: null, defaultResultsPerPage: 25 });
  paginationKeys$ = this.paginationKeys.asObservable();
  advanceSearchOptions = new BehaviorSubject<any>({});
  advanceSearchOptions$ = this.advanceSearchOptions.asObservable();
  agentTypeHandler = new BehaviorSubject<any>({});
  agentTypeHandler$ = this.agentTypeHandler.asObservable();
  userTypeHandler = new BehaviorSubject<any>({});
  userTypeHandler$ = this.userTypeHandler.asObservable();
  globalConfigOptions = new BehaviorSubject<any>({});
  globalConfigOptions$ = this.globalConfigOptions.asObservable();
  constructor(
    private pageService: PageService,
    private http: HttpService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  safeParseJSON(jsonData) {
    let parsedData;
    try {
      parsedData = JSON.parse(jsonData);
    } catch (error) {
      console.log('Error occured while parsing data in commonService', error);
    }
    return parsedData;
  }

  // Decode data coming from API
  decodeURIData(data) {
    if (data) {
      return decodeURIComponent(data);
    }
  }

  // Remove html tags from a string, leaving only the inner text
  removeHTMLtags(text) {
    const temp = document.createElement("DIV");
    temp.innerHTML = text;
    return temp.textContent || temp.innerText || "";
  }

  // Delete Cookies from browser
  deleteCookies(cookieNames: string[]) {
    for (const cookieName of cookieNames) {
      this.document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  }

  getCustomLocale(fallBackCCLCValue, pageConfig) {
    if (pageConfig) {
      if (fallBackCCLCValue && typeof fallBackCCLCValue === 'object' && fallBackCCLCValue.hasOwnProperty(pageConfig.locale)) {
        return { 'cc': fallBackCCLCValue[pageConfig.locale].split('-')[0], 'lc': fallBackCCLCValue[pageConfig.locale].split('-')[1] }
      } else {
        return { 'cc': pageConfig['cc'], 'lc': pageConfig['lc'] }
      }
    }
  }

  getDocTypes() {
    return this.http.get(`/wise-services/search/${this.pageConfig.lc}/advSearchOptions`)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((result: any) => {
        if (result && result?.data) {
          this.advanceSearchOptions.next(result.data);
        } else {
          return null;
        }
      });
  }

  mapRCLCList(): Observable<any> {
    return this.http.get(`/wise-services/ssc/regions`).pipe(map(
      (result: any) => {
        if (result) {
          if (result?.data) {
            localStorage.setItem("rclcData", JSON.stringify(result.data));
          }
          return result;
        }
      }
    )
    ).pipe(catchError(() => {
      console.log('Caught in CatchError. Returning 0')
      return of(0);
    })
    )
  }

  // this get call returns types of Agent (Ex. Commercial , Consumer etc)
  agentTypeList() {
    this.http
      .get(`/wise-services/core/agent/getusertypes`)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((result: any) => {
        if (result?.data) {
          localStorage.setItem("agentTypeList", JSON.stringify(result.data));
          this.agentTypeHandler.next(result.data);
        }
      });
  }

  // Get all the active UserType lists
  userTypeList() {
    this.http
      .get(`/wise-services/cms/usertype`)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((result: any) => {
        if (result?.data) {
          localStorage.setItem("userTypeList", JSON.stringify(result.data));
          this.userTypeHandler.next(result.data);
        }
      });
  }

  // Get Usertype based on userdata
  getUserTypeSelection(userData) {
    let userType;
    let agentType = localStorage.getItem('agentType');
    if (userData?.userType === 'AGENT' || userData?.userType === 'CRM_AGENT' || userData?.userType === 'FEDERAL_AGENT') {
      if (agentType === 'commercial') {
        userType = 'COMMERCIAL_AGENT';
      }
      if (agentType === 'consumer') {
        userType = 'CONSUMER_AGENT';
      }
    }
    else if (userData?.userType === 'CHANNEL_PARTNER_USER') {
      if (userData?.a3Authorized) {
        userType = 'CHANNEL_PARTNER_A3';
      }
      else {
        userType = 'CHANNEL_PARTNER_NON_A3';
      }
    }
    else {
      userType = userData?.userType;
    }
    return userType;
  }

  // Get Global Configuration Data
  getGlobalConfig() {
    const obj = {
      "regionCode": this.pageConfig.rc,
      "langCode": this.pageConfig.lc
    };
    return this.http.post('/wise-services/cms/globalconfig', obj)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((result: any) => {
        if (result && result?.data) {
          this.globalConfigOptions.next({ data: result?.data });
          this.typeAheadURL.next({ typeAheadURL: result.data.typeAheadURL });
          this.productTypeAheadURL.next({ productTypeAheadURL: result.data.productTypeAheadURL })
          this.paginationKeys.next({ paginationKeys: result.data.viewsPerPage, defaultResultsPerPage: result.data.defaultResultsPerPage })
        } else {
          return null;
        }
      });
  }

  //no query exist
  contextKeyNavigation(url) {
    let redirectUrl = `${this.pageConfig.domain}/${url}/${this.pageConfig.rc}-${this.pageConfig.lc}`;
    if (this.pageConfig?.getUrl?.match('contextKey=')) {
      redirectUrl = `${redirectUrl}?contextKey=${this.pageConfig?.queryParams?.contextKey}`
    }
    return redirectUrl;
  }

  // query exist in redirection url
  addContextKeyNavigation(url) {
    let redirectUrl = url;
    if (this.pageConfig?.getUrl?.match('contextKey=')) {
      redirectUrl = `${redirectUrl}&contextKey=${this.pageConfig?.queryParams?.contextKey}`;
    }
    window.open(redirectUrl, '_blank');
  }

  //fixed urls
  fixedNavigation(url) {
    let redirectUrl = url;
    if (this.pageConfig.getUrl.match('contextKey=') && url.match('wise')) {
      redirectUrl = `${url}?contextKey=${this.pageConfig.queryParams.contextKey}`;
    }
    window.open(redirectUrl, '_blank');
  }

  // CDAX Data - Get Context Key Details
  getContextKeyDetails(contextFlagParam) {
    let contextArray = [];
    let contextData;
    // Fetch data from context key details
    let contextStorage = JSON.parse(
      localStorage.getItem('contextKeyList')
    );
    const contextKeyMatch = contextStorage?.find(
      (data) =>
        data.contextKeyListUniqueId === contextFlagParam
    );
    contextArray.push(contextKeyMatch?.contextKeyListInfo);
    contextData = contextArray[0];
    return contextData;
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

}
